import React, { createContext, useContext, useState, useEffect } from "react";
import { translations, Language, isRTL } from "@/locales";
import { useGeolocation, displayPricing, regionalPricing, Region, setLocalePreference } from "@/hooks/useGeolocation";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, replacements?: Record<string, string>) => string;
  currency: string;
  currencySymbol: string;
  region: Region;
  isRTL: boolean;
  isAutoDetected: boolean;
  getPrice: (key: string) => number;
  getStripePricing: (key: string) => { price: number; currency: string };
  formatPrice: (amount: number) => string;
}

// Create context with default values to prevent crashes during hot reload
const defaultContextValue: LanguageContextType = {
  language: "en",
  setLanguage: () => {},
  t: (key: string) => key,
  currency: "EUR",
  currencySymbol: "€",
  region: "eu",
  isRTL: false,
  isAutoDetected: true,
  getPrice: () => 0,
  getStripePricing: () => ({ price: 0, currency: "eur" }),
  formatPrice: (amount: number) => `€${amount}`,
};

const LanguageContext = createContext<LanguageContextType>(defaultContextValue);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const geo = useGeolocation();
  // Read persisted locale synchronously so the first paint uses the right language.
  const readSavedLanguage = (): Language | null => {
    try {
      const raw = typeof window !== "undefined"
        ? window.localStorage.getItem("boost72_locale")
        : null;
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const lang = parsed?.language as Language | undefined;
      if (lang && ["en", "de", "fr", "es", "ar"].includes(lang)) return lang;
    } catch {
      /* ignore */
    }
    return null;
  };
  const savedLang = readSavedLanguage();
  const [language, setLanguageState] = useState<Language>(savedLang ?? "en");
  const [isAutoDetected, setIsAutoDetected] = useState(savedLang === null);

  // Keep language in sync with the persisted preference when it changes elsewhere.
  useEffect(() => {
    const lang = readSavedLanguage();
    if (lang && lang !== language) {
      setLanguageState(lang);
      setIsAutoDetected(false);
    }
  }, [geo.language]);

  // Apply RTL direction to document
  useEffect(() => {
    if (isRTL(language)) {
      document.documentElement.dir = "rtl";
      document.documentElement.lang = language;
    } else {
      document.documentElement.dir = "ltr";
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    setIsAutoDetected(false);
    
    // Get the appropriate region for this language
    const langToRegion: Record<Language, Region> = {
      en: "us",
      de: "eu",
      fr: "eu",
      es: "eu",
      ar: "gulf",
    };
    
    const langToCurrency: Record<Language, { currency: string; symbol: string }> = {
      en: { currency: "USD", symbol: "$" },
      de: { currency: "EUR", symbol: "€" },
      fr: { currency: "EUR", symbol: "€" },
      es: { currency: "EUR", symbol: "€" },
      ar: { currency: "QAR", symbol: "ر.ق" },
    };
    
    const currencyInfo = langToCurrency[lang];
    setLocalePreference(lang, langToRegion[lang], currencyInfo.currency, currencyInfo.symbol);
  };

  const t = (key: string, replacements?: Record<string, string>): string => {
    const langTranslations = translations[language] || translations.en;
    let text = langTranslations[key] || translations.en[key] || key;
    
    // Handle replacements like {price}, {symbol}, etc.
    if (replacements) {
      Object.entries(replacements).forEach(([placeholder, value]) => {
        text = text.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), value);
      });
    }
    
    // Auto-replace {symbol} with currency symbol if not provided
    if (text.includes('{symbol}')) {
      text = text.replace(/\{symbol\}/g, geo.currencySymbol || '€');
    }
    
    // Auto-replace {price} with launch pack price if not provided
    if (text.includes('{price}')) {
      const launchPrice = getPrice('launch');
      text = text.replace(/\{price\}/g, formatPrice(launchPrice));
    }
    
    return text;
  };

  const getPrice = (key: string): number => {
    const region = geo.region || "eu";
    const prices = displayPricing[region];
    return prices[key] || displayPricing.eu[key] || 0;
  };

  const getStripePricing = (key: string): { price: number; currency: string } => {
    const region = geo.region || "eu";
    const prices = regionalPricing[region];
    return prices[key] || regionalPricing.eu[key] || { price: 0, currency: "eur" };
  };

  const formatPrice = (amount: number): string => {
    const symbol = geo.currencySymbol || "€";
    // For RTL currencies, show number first then symbol
    if (isRTL(language)) {
      return `${amount} ${symbol}`;
    }
    return `${symbol}${amount}`;
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        currency: geo.currency || "EUR",
        currencySymbol: geo.currencySymbol || "€",
        region: geo.region || "eu",
        isRTL: isRTL(language),
        isAutoDetected,
        getPrice,
        getStripePricing,
        formatPrice,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  return useContext(LanguageContext);
};

// Re-export types for convenience
export type { Language, Region };
